import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

// Question Types Configuration
const QUESTION_TYPES = [
  { 
    value: 'text', 
    label: 'Open Text', 
    icon: '📝',
    description: 'Open-ended text response'
  },
  { 
    value: 'scale', 
    label: 'Sliding Scale', 
    icon: '📊',
    description: 'Rating scale question'
  },
  { 
    value: 'multi', 
    label: 'Multiple Answer', 
    icon: '☑️',
    description: 'Select multiple options'
  },
  { 
    value: 'single', 
    label: 'Multiple Choice', 
    icon: '⚪',
    description: 'Select one option'
  }
];

const PulseBuilderV2 = () => {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [draggedQuestionId, setDraggedQuestionId] = useState(null);
  
  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [surveySettings, setSurveySettings] = useState({
    title: '',
    description: '',
    anonymous: false
  });
  
  // Share modal state  
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [availableUsers] = useState([
    { id: 1, name: 'John Smith', email: 'john.smith@company.com' },
    { id: 2, name: 'Jane Doe', email: 'jane.doe@company.com' },
    { id: 3, name: 'Mike Johnson', email: 'mike.johnson@company.com' },
    { id: 4, name: 'Sarah Wilson', email: 'sarah.wilson@company.com' },
    { id: 5, name: 'David Brown', email: 'david.brown@company.com' }
  ]);

  // Get the currently selected question
  const selectedQuestion = questions.find(q => q.id === selectedQuestionId);

  // Add a new question
  const handleAddQuestion = useCallback((type) => {
    const newQuestion = {
      id: uuidv4(),
      question: '',
      type: type,
      aiEnhanced: true,
      task_type: 'question',
      scale: type === 'scale' ? '0-10' : null,
      qualifier: type === 'scale' ? ['Strongly Disagree', 'Neutral', 'Strongly Agree'] : null,
      options: (type === 'multi' || type === 'single') ? [''] : null,
    };

    setQuestions(prev => [...prev, newQuestion]);
    setSelectedQuestionId(newQuestion.id);
    setShowAddDropdown(false);
  }, []);

  // Update a question
  const handleQuestionUpdate = useCallback((field, value) => {
    if (!selectedQuestionId) return;

    setQuestions(prev => prev.map(q => {
      if (q.id !== selectedQuestionId) return q;

      if (field === 'type' && value === 'scale') {
        return {
          ...q,
          [field]: value,
          scale: '0-10',
          qualifier: ['Strongly Disagree', 'Neutral', 'Strongly Agree'],
          options: null
        };
      } else if (field === 'type' && (value === 'multi' || value === 'single')) {
        return {
          ...q,
          [field]: value,
          options: q.options || [''],
          scale: null,
          qualifier: null
        };
      } else {
        return { ...q, [field]: value };
      }
    }));
  }, [selectedQuestionId]);

  // Handle option changes for multi/single choice questions
  const handleOptionChange = useCallback((optionIndex, value) => {
    if (!selectedQuestion) return;

    const newOptions = [...(selectedQuestion.options || [])];
    newOptions[optionIndex] = value;

    setQuestions(prev => prev.map(q => 
      q.id === selectedQuestionId 
        ? { ...q, options: newOptions }
        : q
    ));
  }, [selectedQuestionId, selectedQuestion]);

  const handleAddOption = useCallback(() => {
    if (!selectedQuestion) return;

    const newOptions = [...(selectedQuestion.options || []), ''];
    setQuestions(prev => prev.map(q => 
      q.id === selectedQuestionId 
        ? { ...q, options: newOptions }
        : q
    ));
  }, [selectedQuestionId, selectedQuestion]);

  const handleRemoveOption = useCallback((optionIndex) => {
    if (!selectedQuestion || !selectedQuestion.options) return;

    const newOptions = selectedQuestion.options.filter((_, index) => index !== optionIndex);
    setQuestions(prev => prev.map(q => 
      q.id === selectedQuestionId 
        ? { ...q, options: newOptions }
        : q
    ));
  }, [selectedQuestionId, selectedQuestion]);

  // Handle qualifier changes for scale questions
  const handleQualifierChange = useCallback((position, value) => {
    if (!selectedQuestion) return;

    const newQualifiers = [...(selectedQuestion.qualifier || [])];
    newQualifiers[position] = value;

    setQuestions(prev => prev.map(q => 
      q.id === selectedQuestionId 
        ? { ...q, qualifier: newQualifiers }
        : q
    ));
  }, [selectedQuestionId, selectedQuestion]);

  // Remove a question
  const handleRemoveQuestion = useCallback((questionId) => {
    setQuestions(prev => prev.filter(q => q.id !== questionId));
    if (selectedQuestionId === questionId) {
      setSelectedQuestionId(null);
    }
  }, [selectedQuestionId]);

  // Drag and drop handlers
  const handleDragStart = useCallback((questionId) => {
    setDraggedQuestionId(questionId);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e, targetQuestionId) => {
    e.preventDefault();
    
    if (!draggedQuestionId || draggedQuestionId === targetQuestionId) {
      setDraggedQuestionId(null);
      return;
    }

    const draggedIndex = questions.findIndex(q => q.id === draggedQuestionId);
    const targetIndex = questions.findIndex(q => q.id === targetQuestionId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedQuestionId(null);
      return;
    }

    const newQuestions = [...questions];
    const [draggedQuestion] = newQuestions.splice(draggedIndex, 1);
    newQuestions.splice(targetIndex, 0, draggedQuestion);

    setQuestions(newQuestions);
    setDraggedQuestionId(null);
  }, [draggedQuestionId, questions]);

  const getQuestionTypeLabel = (type) => {
    const questionType = QUESTION_TYPES.find(qt => qt.value === type);
    return questionType ? questionType.label : type;
  };

  // Handle settings changes
  const handleSettingsChange = useCallback((field, value) => {
    setSurveySettings(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    const surveyData = {
      settings: surveySettings,
      questions: questions,
      sharedUsers: sharedUsers
    };
    console.log('Saving survey:', surveyData);
    // Here you would typically make an API call to save the survey
  }, [surveySettings, questions, sharedUsers]);

  // Handle share functionality
  const handleAddSharedUser = useCallback((userId) => {
    const user = availableUsers.find(u => u.id === userId);
    if (user && !sharedUsers.find(su => su.id === userId)) {
      setSharedUsers(prev => [...prev, user]);
    }
  }, [availableUsers, sharedUsers]);

  const handleRemoveSharedUser = useCallback((userId) => {
    setSharedUsers(prev => prev.filter(u => u.id !== userId));
  }, []);

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderContent>
          <HeaderLeft>
            <Title>Pulse Builder</Title>
            <Subtitle>Design your survey questions</Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButton onClick={() => setShowSettingsModal(true)}>
              <SettingsIcon>⚙️</SettingsIcon>
              Settings
            </HeaderButton>
            <SaveButton onClick={handleSave}>
              <SaveIcon>💾</SaveIcon>
              Save Template
            </SaveButton>
          </HeaderRight>
        </HeaderContent>
      </Header>

      {/* Main Content */}
      <MainContent>
        {/* Left Panel - Question List */}
        <LeftPanel>
          <PanelHeader>
            <PanelTitle>Content</PanelTitle>
            <AddContentWrapper>
              <AddContentButton 
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                active={showAddDropdown}
              >
                <AddIcon>+</AddIcon>
                Add content
              </AddContentButton>
              
              {showAddDropdown && (
                <AddContentDropdown>
                  <DropdownSection>
                    <DropdownSectionTitle>Standard Questions</DropdownSectionTitle>
                    {QUESTION_TYPES.map(type => (
                      <DropdownItem 
                        key={type.value}
                        onClick={() => handleAddQuestion(type.value)}
                      >
                        <DropdownItemIcon>{type.icon}</DropdownItemIcon>
                        <DropdownItemContent>
                          <DropdownItemTitle>{type.label}</DropdownItemTitle>
                          <DropdownItemDescription>{type.description}</DropdownItemDescription>
                        </DropdownItemContent>
                      </DropdownItem>
                    ))}
                  </DropdownSection>
                </AddContentDropdown>
              )}
            </AddContentWrapper>
          </PanelHeader>

          <QuestionList>
            {questions.length === 0 ? (
              <EmptyState>
                <EmptyIcon>📝</EmptyIcon>
                <EmptyTitle>No content yet</EmptyTitle>
                <EmptyText>Add content using the button above</EmptyText>
              </EmptyState>
            ) : (
              questions.map((question, index) => (
                <QuestionListItem
                  key={question.id}
                  draggable
                  onDragStart={() => handleDragStart(question.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, question.id)}
                  selected={selectedQuestionId === question.id}
                  onClick={() => setSelectedQuestionId(question.id)}
                >
                  <QuestionListItemContent>
                    <QuestionNumber>{index + 1}</QuestionNumber>
                    <QuestionListItemText>
                      <QuestionListItemTitle>
                        {question.question || 'Untitled'}
                      </QuestionListItemTitle>
                      <QuestionListItemType>
                        {getQuestionTypeLabel(question.type)}
                      </QuestionListItemType>
                    </QuestionListItemText>
                    <DragHandle>⋮⋮</DragHandle>
                  </QuestionListItemContent>
                </QuestionListItem>
              ))
            )}
          </QuestionList>
        </LeftPanel>

        {/* Center Panel - Question Editor */}
        <CenterPanel>
          {selectedQuestion ? (
            <QuestionEditor>
              <QuestionEditorHeader>
                <QuestionEditorTitle>Question #{questions.findIndex(q => q.id === selectedQuestion.id) + 1}</QuestionEditorTitle>
                <RemoveQuestionButton onClick={() => handleRemoveQuestion(selectedQuestion.id)}>
                  <DeleteIcon>🗑️</DeleteIcon>
                </RemoveQuestionButton>
              </QuestionEditorHeader>

              <QuestionEditorContent>
                {/* Question Title */}
                <FormGroup>
                  <Label>Your question title here</Label>
                  <Input
                    type="text"
                    value={selectedQuestion.question}
                    onChange={(e) => handleQuestionUpdate('question', e.target.value)}
                    placeholder="Enter your question"
                  />
                  <HelpText>(Add an optional description)</HelpText>
                </FormGroup>

                {/* Question Type */}
                <FormGroup>
                  <Label>Question Type</Label>
                  <Select
                    value={selectedQuestion.type}
                    onChange={(e) => handleQuestionUpdate('type', e.target.value)}
                  >
                    {QUESTION_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                {/* Scale Options */}
                {selectedQuestion.type === 'scale' && (
                  <>
                    <FormGroup>
                      <Label>Scale</Label>
                      <Select
                        value={selectedQuestion.scale}
                        onChange={(e) => handleQuestionUpdate('scale', e.target.value)}
                      >
                        <option value="0-10">10 Point</option>
                        <option value="0-5">5 Point</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Qualifiers</Label>
                      <QualifierInputs>
                        <QualifierGroup>
                          <QualifierLabel>Minimum</QualifierLabel>
                          <Input
                            type="text"
                            value={selectedQuestion.qualifier?.[0] || ''}
                            onChange={(e) => handleQualifierChange(0, e.target.value)}
                            placeholder="Strongly Disagree"
                          />
                        </QualifierGroup>
                        <QualifierGroup>
                          <QualifierLabel>Middle</QualifierLabel>
                          <Input
                            type="text"
                            value={selectedQuestion.qualifier?.[1] || ''}
                            onChange={(e) => handleQualifierChange(1, e.target.value)}
                            placeholder="Neutral"
                          />
                        </QualifierGroup>
                        <QualifierGroup>
                          <QualifierLabel>End</QualifierLabel>
                          <Input
                            type="text"
                            value={selectedQuestion.qualifier?.[2] || ''}
                            onChange={(e) => handleQualifierChange(2, e.target.value)}
                            placeholder="Strongly Agree"
                          />
                        </QualifierGroup>
                      </QualifierInputs>
                    </FormGroup>
                  </>
                )}

                {/* Multiple Choice Options */}
                {(selectedQuestion.type === 'multi' || selectedQuestion.type === 'single') && (
                  <FormGroup>
                    <Label>Options</Label>
                    <OptionsContainer>
                      {(selectedQuestion.options || []).map((option, index) => (
                        <OptionRow key={index}>
                          <Input
                            type="text"
                            value={option}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            placeholder={`Option ${index + 1}`}
                          />
                          {selectedQuestion.options && selectedQuestion.options.length > 1 && (
                            <RemoveOptionButton onClick={() => handleRemoveOption(index)}>
                              ×
                            </RemoveOptionButton>
                          )}
                        </OptionRow>
                      ))}
                      <AddOptionButton onClick={handleAddOption}>
                        + Add option
                      </AddOptionButton>
                    </OptionsContainer>
                  </FormGroup>
                )}

                {/* AI Enhanced Toggle */}
                <FormGroup>
                  <ToggleRow>
                    <ToggleLabel>AI Enhanced</ToggleLabel>
                    <Toggle
                      type="checkbox"
                      checked={selectedQuestion.aiEnhanced}
                      onChange={(e) => handleQuestionUpdate('aiEnhanced', e.target.checked)}
                    />
                  </ToggleRow>
                  <HelpText>Use an AI-designed follow-up question to encourage elaboration</HelpText>
                </FormGroup>
              </QuestionEditorContent>
            </QuestionEditor>
          ) : (
            <NoQuestionSelected>
              <NoQuestionIcon>📝</NoQuestionIcon>
              <NoQuestionTitle>No content selected</NoQuestionTitle>
              <NoQuestionText>Select a content item from the list on the left to preview and edit it here.</NoQuestionText>
            </NoQuestionSelected>
          )}
        </CenterPanel>
      </MainContent>

      {/* Settings Modal */}
      {showSettingsModal && (
        <ModalOverlay onClick={() => setShowSettingsModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Survey Settings</ModalTitle>
              <CloseButton onClick={() => setShowSettingsModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalContent>
              <FormGroup>
                <Label>Survey Title</Label>
                <Input
                  type="text"
                  value={surveySettings.title}
                  onChange={(e) => handleSettingsChange('title', e.target.value)}
                  placeholder="Enter survey title"
                />
              </FormGroup>

              <FormGroup>
                <Label>Survey Description</Label>
                <TextArea
                  value={surveySettings.description}
                  onChange={(e) => handleSettingsChange('description', e.target.value)}
                  placeholder="Enter survey description"
                  rows={4}
                />
              </FormGroup>

              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Make this survey anonymous</ToggleLabel>
                  <Toggle
                    type="checkbox"
                    checked={surveySettings.anonymous}
                    onChange={(e) => handleSettingsChange('anonymous', e.target.checked)}
                  />
                </ToggleRow>
                <HelpText>Anonymous surveys don't collect respondent identities</HelpText>
              </FormGroup>

              <FormGroup>
                <Label>Share Access</Label>
                <ShareSection>
                  <ShareDropdownContainer>
                    <Select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddSharedUser(parseInt(e.target.value));
                          e.target.value = '';
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>Select user to share with...</option>
                      {availableUsers
                        .filter(user => !sharedUsers.find(su => su.id === user.id))
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))
                      }
                    </Select>
                  </ShareDropdownContainer>
                  
                  <SharedUsersList>
                    {sharedUsers.map(user => (
                      <SharedUserItem key={user.id}>
                        <SharedUserInfo>
                          <SharedUserName>{user.name}</SharedUserName>
                          <SharedUserEmail>{user.email}</SharedUserEmail>
                        </SharedUserInfo>
                        <RemoveUserButton onClick={() => handleRemoveSharedUser(user.id)}>
                          ×
                        </RemoveUserButton>
                      </SharedUserItem>
                    ))}
                    {sharedUsers.length === 0 && (
                      <EmptySharedUsers>No users have access yet</EmptySharedUsers>
                    )}
                  </SharedUsersList>
                </ShareSection>
              </FormGroup>
            </ModalContent>
            
            <ModalFooter>
              <ModalCancelButton onClick={() => setShowSettingsModal(false)}>
                Cancel
              </ModalCancelButton>
              <ModalSaveButton onClick={() => setShowSettingsModal(false)}>
                Save Settings
              </ModalSaveButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default PulseBuilderV2;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  font-family: "Poppins", sans-serif;
`;

const Header = styled.div`
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const SettingsIcon = styled.span`
  font-size: 14px;
`;

const SaveIcon = styled.span`
  font-size: 14px;
`;

const Title = styled.h1`
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const MainContent = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 300px 1fr;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  min-height: 0;
`;

const LeftPanel = styled.div`
  background: white;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
`;

const CenterPanel = styled.div`
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  padding: 24px;
  min-height: calc(100vh - 80px);
`;



const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const PanelTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const AddContentWrapper = styled.div`
  position: relative;
  margin-top: 12px;
`;

const AddContentButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.active ? '#476dfa' : '#1a202c'};
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#3d5ce6' : '#2d3748'};
  }
`;

const AddIcon = styled.span`
  font-weight: 600;
  font-size: 16px;
`;

const AddContentDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
`;

const DropdownSection = styled.div`
  padding: 8px 0;
`;

const DropdownSectionTitle = styled.div`
  padding: 8px 16px;
  font-weight: 600;
  font-size: 12px;
  color: #7e7e7e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const DropdownItemIcon = styled.span`
  font-size: 16px;
  width: 20px;
  text-align: center;
`;

const DropdownItemContent = styled.div`
  flex: 1;
`;

const DropdownItemTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 2px;
`;

const DropdownItemDescription = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const QuestionList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const QuestionListItem = styled.div`
  background: ${props => props.selected ? '#f0f4ff' : 'white'};
  border: 1px solid ${props => props.selected ? '#476dfa' : '#e0e0e0'};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    background: #f0f4ff;
  }
`;

const QuestionListItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
`;

const QuestionNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #f8f9fa;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
  color: #7e7e7e;
  flex-shrink: 0;
`;

const QuestionListItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuestionListItemTitle = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuestionListItemType = styled.div`
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const DragHandle = styled.div`
  color: #c0c0c0;
  cursor: grab;
  font-size: 12px;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const EmptyText = styled.p`
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const QuestionEditor = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const QuestionEditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const QuestionEditorTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const RemoveQuestionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #f87171;
  }
`;

const DeleteIcon = styled.span`
  font-size: 14px;
`;

const QuestionEditorContent = styled.div`
  padding: 20px;
  flex: 1;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #7e7e7e;
  margin-top: 4px;
  font-style: italic;
`;

const QualifierInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`;

const QualifierGroup = styled.div``;

const QualifierLabel = styled.label`
  display: block;
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 6px;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveOptionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #dc3545;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #f87171;
  }
`;

const AddOptionButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  border: 1px dashed #c0c0c0;
  border-radius: 6px;
  color: #476dfa;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    background: #f0f4ff;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const Toggle = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const NoQuestionSelected = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 48px 24px;
`;

const NoQuestionIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const NoQuestionTitle = styled.h3`
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0 0 8px 0;
`;

const NoQuestionText = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 20px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const ModalContent = styled.div`
  padding: 20px 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
`;

const ModalCancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const ModalSaveButton = styled.button`
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

// Share Components
const ShareSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShareDropdownContainer = styled.div`
  width: 100%;
`;

const SharedUsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const SharedUserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
`;

const SharedUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SharedUserName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
`;

const SharedUserEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const RemoveUserButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #dc3545;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #f87171;
  }
`;

const EmptySharedUsers = styled.div`
  padding: 20px;
  text-align: center;
  color: #7e7e7e;
  font-family: "Poppins";
  font-size: 13px;
  font-style: italic;
`;


